import { useState, useRef, useEffect } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreateTagDialog } from "@/components/tags/CreateTagDialog";
import { TagList } from "@/components/tags/TagList";
import { TagGrid } from "@/components/tags/TagGrid";
import ViewToggle from "@/components/ViewToggle";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";

interface Tag {
  id: string;
  name: string;
  description: string;
  type: string;
  color: string;
  icon?: string;
  criteria: any;
  created_at: string;
  created_by: string;
  customers: string[];
}

export default function TagsPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("tagsViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "grid";
  });
  const tagListRef = useRef<{ refreshTags: () => void }>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: apiTags = [], isLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: () => tagsApi.listTags(),
  });

  // Map API tags to component Tag format
  const tags: Tag[] = apiTags.map((t: any) => ({
    id: t.tag_id || t.tagId || t.id || "",
    name: t.tag_name || t.tagName || t.name || "",
    description: t.tag_description || t.description || "",
    type: t.tag_type || t.tagType || t.type || "static",
    color: t.tag_color || t.color || "#6b7280",
    icon: t.tag_icon || t.icon,
    criteria: t.tag_criteria || t.criteria || null,
    created_at: t.created_at || t.createdAt || new Date().toISOString(),
    created_by: t.created_by || t.createdBy || "system",
    customers: t.customers || [],
  }));

  const deleteTag = async (id: string) => {
    try {
      await tagsApi.deleteTagById(id);
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      toast({
        title: "Tag deleted",
        description: "Tag has been deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast({
        title: "Error",
        description: "Failed to delete tag. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTagCreated = () => {
    setIsCreateDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ["tags"] });
  };

  useEffect(() => {
    localStorage.setItem("tagsViewPreference", view);
  }, [view]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 p-6 w-full">
      <div className="w-full mx-auto space-y-6">
        <div className="flex justify-between items-start mb-8">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Tags</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              Organize devices with static tags or create dynamic tags with custom criteria
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <ViewToggle view={view} onViewChange={setView} />
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Tag
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {view === "list" ? (
              <TagList ref={tagListRef} searchQuery={searchQuery} />
            ) : (
              <TagGrid 
                tags={tags.filter((tag) =>
                  tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  tag.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  tag.type.toLowerCase().includes(searchQuery.toLowerCase())
                )}
                onDelete={deleteTag}
              />
            )}
          </>
        )}

        <CreateTagDialog 
          open={isCreateDialogOpen} 
          onOpenChange={setIsCreateDialogOpen}
          onTagCreated={handleTagCreated}
        />
      </div>
    </div>
  );
}
